import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';
import type { AdminUser } from '../types/admin';

interface AdminAuthContextType {
  adminUser: AdminUser | null;
  loading: boolean;
  isAdmin: boolean;
  checkAdminAccess: () => Promise<boolean>;
  logout: () => void;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const checkAdminAccess = async (): Promise<boolean> => {
    if (!user) {
      setIsAdmin(false);
      setAdminUser(null);
      return false;
    }

    try {
      const adminDocRef = doc(db, 'adminUsers', user.uid);
      const adminDoc = await getDoc(adminDocRef);

      if (adminDoc.exists()) {
        const adminData = adminDoc.data() as AdminUser;
        
        // Check if admin is active
        if (!adminData.isActive) {
          setIsAdmin(false);
          setAdminUser(null);
          return false;
        }

        setAdminUser({
          ...adminData,
          id: adminDoc.id
        });
        setIsAdmin(true);

        return true;
      } else {
        setIsAdmin(false);
        setAdminUser(null);
        return false;
      }
    } catch (error: any) {
      // Silently handle errors - most users aren't admins, this is expected
      if (error.code !== 'unavailable' && !error.message?.includes('offline')) {
        console.warn('⚠ Admin access check failed:', error.message);
      }
      setIsAdmin(false);
      setAdminUser(null);
      return false;
    }
  };

  const logout = () => {
    setAdminUser(null);
    setIsAdmin(false);
  };

  useEffect(() => {
    let isMounted = true;
    
    const initAdminAuth = async () => {
      if (!user) {
        if (isMounted) {
          setLoading(false);
        }
        return;
      }

      if (isMounted) {
        setLoading(true);
      }
      
      try {
        await checkAdminAccess();
      } catch (error) {
        // Silently handle - errors are already logged in checkAdminAccess
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initAdminAuth();
    
    return () => {
      isMounted = false;
    };
  }, [user?.uid]); // Only depend on user ID, not entire user object

  return (
    <AdminAuthContext.Provider value={{ 
      adminUser, 
      loading, 
      isAdmin, 
      checkAdminAccess,
      logout 
    }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
}
